import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { bmsAxios } from '../../common/axios';
import { PmHttpResponse, SelectOption } from '../../common/common.model';
import {
  ConeProduct,
  ConeUserAccessPermissions,
  ConeUserRole,
} from '../../common/constants/common';
import { PartnerStackService } from '../../common/partnerStackHelper';
import { isP2PProductUser, isPmProductUser } from '../../common/roleHelper';
import { processTaxSymbolsResponse } from '../../common/utils';
import { UtmService } from '../../common/utmHelper';
import {
  BannerDto,
  InfoBannerMap,
  TaxSymbolsResponse,
  TaxSymbolsResponseTransformed,
  UserMeta,
  UserWorkPermissions,
} from './models';

export class PmServices {
  public static getUserMeta() {
    return bmsAxios
      .get<PmHttpResponse<UserMeta>>('/users/meta')
      .then((res) => res.data.data);
  }

  public static pmOrgSetup() {
    return bmsAxios
      .post<PmHttpResponse<UserMeta>>('/org/setup', {
        value: ConeProduct.PM,
      })
      .then((res) => res.data.data);
  }

  public static p2pOrgSetup() {
    return bmsAxios
      .post<PmHttpResponse<UserMeta>>('/org/setup', {
        value: ConeProduct.P2P,
      })
      .then((res) => res.data.data);
  }

  public static getBannerStatus() {
    return bmsAxios
      .get<PmHttpResponse<Record<InfoBannerMap, boolean>>>('banner_status')
      .then((res) => res.data.data);
  }

  public static updateBannerStatus(payload: Record<string, boolean>) {
    return bmsAxios.post<PmHttpResponse<void>>('banner_status', payload);
  }

  static syncPartnerStackReferral() {
    const referralData = PartnerStackService.getFromStorage();
    const utmData = UtmService.getFromStorage();

    if (referralData || utmData) {
      return bmsAxios
        .post<PmHttpResponse<null>>('org/referral', {
          ps_xid: referralData?.psId || undefined,
          ps_partner_key: referralData?.psPartnerKey || undefined,
          utm_data: utmData || undefined,
        })
        .then(() => undefined);
    }

    return Promise.resolve();
  }

  static getUserFeatureFlags() {
    return bmsAxios
      .get<PmHttpResponse<string[]>>('org/feature-flags')
      .then((res) => res.data.data);
  }

  static getCountries() {
    return bmsAxios
      .get<PmHttpResponse<{ [key: string]: string }[]>>('values/countries')
      .then((res) =>
        Object.entries(res.data.data).map(
          ([key, value]) =>
            ({
              label: key,
              value,
            }) as unknown as SelectOption,
        ),
      );
  }

  static getPhoneCodes() {
    return bmsAxios
      .get<PmHttpResponse<{ [key: string]: string }[]>>('values/phone-codes')
      .then((res) =>
        Object.entries(res.data.data).map(
          ([key, value]) =>
            ({
              label: value,
              value,
              original: key,
            }) as unknown as SelectOption,
        ),
      );
  }

  static getTaxSymbols() {
    return bmsAxios
      .get<PmHttpResponse<TaxSymbolsResponse[]>>('clients/tax-symbols')
      .then((res) => res.data.data)
      .then((data) => {
        const uniqueSymbols = processTaxSymbolsResponse(data);
        return uniqueSymbols.map(
          (item) =>
            ({
              label: item.enum.toUpperCase().replace(/_/g, ' '),
              value: item.enum,
              original: item,
            }) as SelectOption<
              TaxSymbolsResponseTransformed['enum'],
              TaxSymbolsResponseTransformed
            >,
        );
      });
  }

  static getBanners() {
    return bmsAxios
      .get<PmHttpResponse<BannerDto[]>>('banners')
      .then((res) => res.data.data);
  }

  static getUserWorkPermission() {
    return bmsAxios
      .get<PmHttpResponse<UserWorkPermissions>>('permissions/work')
      .then((res) => res.data.data);
  }
}

export const resendAuth0VerificationEmail = () =>
  bmsAxios.post<PmHttpResponse<void>>('/users/verify-email');

export const PM_SERVICES_QUERY_KEYS = {
  GET_USER_META: 'getUserMeta',
  GET_BANNER_STATUS: 'getBannerStatus',
  GET_USER_FEATURE_FLAGS: 'getUserFeatureFlags',
  GET_COUNTRIES: 'getCountries',
  GET_PHONE_CODES: 'getPhoneCodes',
  GET_TAX_SYMBOLS: 'getTaxSymbols',
  GET_BANNERS: 'getBanners',
  GET_USER_WORK_PERMISSION: 'getUserWorkPermission',
};

export const useGetUserMeta = (enabled?: boolean) =>
  useQuery({
    queryKey: [PM_SERVICES_QUERY_KEYS.GET_USER_META],
    queryFn: PmServices.getUserMeta,
    staleTime: 24 * 60 * 60 * 1000, // 24 hours
    enabled,
  });

export const useGetUserWorkPermissions = () =>
  useQuery({
    queryKey: [PM_SERVICES_QUERY_KEYS.GET_USER_WORK_PERMISSION],
    queryFn: PmServices.getUserWorkPermission,
  });

export const useGetBannerStatus = () =>
  useQuery({
    queryKey: [PM_SERVICES_QUERY_KEYS.GET_BANNER_STATUS],
    queryFn: PmServices.getBannerStatus,

    staleTime: 1 * 60 * 60 * 1000, // 1 hours
  });

export const useUpdateBannerStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: PmServices.updateBannerStatus,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PM_SERVICES_QUERY_KEYS.GET_BANNER_STATUS],
      });
    },
  });
};

export const usePmOrgSetup = () =>
  useMutation({ mutationFn: PmServices.pmOrgSetup });

export const useP2POrgSetup = () =>
  useMutation({ mutationFn: PmServices.p2pOrgSetup });

export const useGetUserRoles = (enabled?: boolean) => {
  const { data: userMeta, isLoading } = useGetUserMeta(enabled);

  if (isLoading) {
    return undefined;
  }

  return userMeta?.roles || [];
};

export const useUserPermissions = (enabled?: boolean) => {
  const { data: userMeta } = useGetUserMeta(enabled);

  return userMeta?.org_permissions || [];
};

export const useGetSimplifiedUserRole = (enabled?: boolean) => {
  const roles = useGetUserRoles(enabled);
  const userPermission = useUserPermissions(enabled);

  const isAdmin = useMemo(() => roles?.includes(ConeUserRole.ADMIN), [roles]);

  const isOwner = useMemo(
    () => Boolean(roles?.includes(ConeUserRole.OWNER)),
    [roles],
  );

  const hasBilling = useMemo(
    () =>
      userPermission?.some((permission) =>
        [ConeUserAccessPermissions.SETTINGS_VIEW_BILLING].includes(permission),
      ),
    [userPermission],
  );

  return { isAdmin, hasBilling, isOwner };
};

export const useGetElementsToShowAsPerRole = (enabled?: boolean) => {
  const userPermission = useUserPermissions(enabled);
  const { isAdmin, hasBilling, isOwner } = useGetSimplifiedUserRole(enabled);

  return useMemo(
    () => ({
      showTemplates: userPermission?.includes(
        ConeUserAccessPermissions.TEMPLATE_VIEW,
      ),
      showAdminSettings: isOwner || isAdmin,
      showBilling: isOwner || hasBilling,
      showDashboard: isOwner,
    }),
    [isOwner, isAdmin, hasBilling, userPermission],
  );
};

export const useGetProductAccess = (enabled?: boolean) => {
  const { data: userMeta } = useGetUserMeta(enabled);

  return useMemo(() => {
    const hasPmAccess = isPmProductUser(userMeta);
    const hasP2PAccess = isP2PProductUser(userMeta);
    const hasOnlyPmAccess = hasPmAccess && !hasP2PAccess;

    return {
      hasPmAccess,
      hasP2PAccess,
      hasOnlyPmAccess,
    };
  }, [userMeta]);
};

export const useSyncPartnerStackReferral = () =>
  useMutation({
    mutationFn: PmServices.syncPartnerStackReferral,
    onSuccess: () => {
      PartnerStackService.removeFromStorage();
      UtmService.removeFromStorage();
    },
  });

export const useGetDashboardPermission = (enabled?: boolean) => {
  const { isOwner, isAdmin } = useGetSimplifiedUserRole(enabled);

  return useMemo(() => isOwner || isAdmin, [isAdmin, isOwner]);
};

export const useGetTabPermissions = (enabled?: boolean) => {
  const userPermission = useUserPermissions(enabled);

  return useMemo(
    () => ({
      paymentsTab: userPermission.includes(
        ConeUserAccessPermissions.TAB_PAYMENTS,
      ),
      billingsTab: userPermission.includes(
        ConeUserAccessPermissions.TAB_BILLING,
      ),
      templatesTab: userPermission.includes(
        ConeUserAccessPermissions.TAB_TEMPLATES,
      ),
      contactsTab: userPermission.includes(
        ConeUserAccessPermissions.TAB_CONTACTS,
      ),
      dealsTab: userPermission.includes(ConeUserAccessPermissions.TAB_DEALS),
      workTab: userPermission.includes(ConeUserAccessPermissions.TAB_WORK),
      servicesTab: userPermission.includes(
        ConeUserAccessPermissions.TAB_SERVICES,
      ),
      proposalsTab: userPermission.includes(
        ConeUserAccessPermissions.TAB_PROPOSALS,
      ),
      timeTab: userPermission.includes(ConeUserAccessPermissions.TAB_TIME),
      settingsTab: userPermission.includes(
        ConeUserAccessPermissions.TAB_SETTINGS,
      ),
      reportsTab: userPermission.includes(
        ConeUserAccessPermissions.TAB_REPORTS,
      ),
      filesTab: userPermission.includes(ConeUserAccessPermissions.TAB_FILES),
    }),
    [userPermission],
  );
};

export const useGetUserFeatureFlags = (enabled: boolean = true) =>
  useQuery({
    queryKey: [PM_SERVICES_QUERY_KEYS.GET_USER_FEATURE_FLAGS],
    queryFn: PmServices.getUserFeatureFlags,
    staleTime: 24 * 60 * 60 * 1000, // 24 hours
    enabled,
  });

export const useGetCountries = (enabled?: boolean) =>
  useQuery({
    queryKey: [PM_SERVICES_QUERY_KEYS.GET_COUNTRIES],
    queryFn: PmServices.getCountries,
    staleTime: 24 * 60 * 60 * 1000, // 24 hours
    enabled,
  });

export const useGetPhoneCodes = (enabled?: boolean) =>
  useQuery({
    queryKey: [PM_SERVICES_QUERY_KEYS.GET_PHONE_CODES],
    queryFn: PmServices.getPhoneCodes,
    staleTime: 24 * 60 * 60 * 1000, // 24 hours
    enabled,
  });

export const useGetTaxSymbols = (enabled?: boolean) =>
  useQuery({
    queryKey: [PM_SERVICES_QUERY_KEYS.GET_TAX_SYMBOLS],
    queryFn: PmServices.getTaxSymbols,
    enabled,
  });

export const useGetBanners = (enabled: boolean = true) =>
  useQuery({
    queryKey: [PM_SERVICES_QUERY_KEYS.GET_BANNERS],
    queryFn: PmServices.getBanners,
    staleTime: 1 * 60 * 60 * 1000, // 1 hours
    enabled,
  });

export const useResendAuth0VerificationEmail = () =>
  useMutation({
    mutationFn: resendAuth0VerificationEmail,
  });
